import React, { useCallback, useEffect, useState } from 'react';
import CircularProgress from '@mui/material/CircularProgress';
import Stack from '@mui/material/Stack';
import { useTranslation } from 'react-i18next';
import { selectVersionsData } from '../../store/general/general-slice-selectors';
import { resetVersionVersionsRestState } from '../../store/general/general-slice';
import { createUpdateVersion } from '../../store/general/general-actions';
import { useAppDispatch, useAppSelector } from '../../store';
import useServerValidationResponseEffect from '@common/hooks/useServerValidationResponseEffect';
import HttpFetchStatus from '@common/enums/api/http-fetch-status';
import useValidatedInputEffect from '@common/hooks/useValidatedInputEffect';
import CustomAlert from '@common/components/Feedback/CustomAlert';
import { isHttpError } from '@common/interfaces/api/http-error';
import TextInput from '@common/components/Inputs/TextInput';
import { AddIcon, CrossCloseIcon, MoveRightArrow, SaveIcon } from '@common/utils/icons/icons';
import CustomSnackbar from '@common/components/Feedback/CustomSnackbar';
import VersionedPackage from '../../models/enums/versioned-package';
import IconButton from '@mui/material/IconButton';
import SelectInput from '../forms/SelectInput';
import ValidationResult from '@common/interfaces/api/validation-result';
import { SelectChangeEvent } from '@mui/material';
import Grid from '@mui/material/Grid';
import InputLabel from '@mui/material/InputLabel';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import TooltipIconButton from '@common/components/Inputs/TooltipIconButton';
import { sortByDate } from '@common/utils/date';

const VersionsForm: React.FC<{
  label: string;
  initialValueId?: string;
  value: string;
  onChange: (event: SelectChangeEvent<unknown>, child: React.ReactNode) => void;
  setValue: (id: string) => void;
  validationResult: ValidationResult;
  versionPackage: VersionedPackage;
}> = ({ label, value, initialValueId, setValue, onChange, validationResult, versionPackage }) => {
  const { t } = useTranslation();

  const dispatch = useAppDispatch();

  const versionsData = useAppSelector((state) => selectVersionsData(state));

  const [isCustomVersionFormOpen, setIsCustomVersionFormOpen] = useState(false);

  const { resetToggle, updateResetToggle, removeServerError, getServerValidationResultOrNull } =
    useServerValidationResponseEffect({
      postError: versionsData.versions.rest.postError,
      resetPostHandler: () =>
        dispatch(
          resetVersionVersionsRestState({ postStatus: HttpFetchStatus.Idle, postError: null }),
        ),
    });

  const {
    value: version,
    validationResult: versionValidationResult,
    valueChangeHandler: versionChangeHandler,
  } = useValidatedInputEffect(
    '',
    (newValue) => ({ isValid: true, error: t('form-help-text-version-version') }),
    (_) => () => {},
    getServerValidationResultOrNull('version'),
    removeServerError,
    resetToggle,
  );

  const getVersionById = (id: string | undefined) => {
    return versionsData.versions.data?.find((v) => v.id === id)?.version ?? t('info-not-set');
  };

  const openCustomVersionFormClickHandler = () => {
    setIsCustomVersionFormOpen(true);
  };

  const closeCustomVersionFormHandler = useCallback(() => {
    setIsCustomVersionFormOpen(false);
  }, []);

  useEffect(() => {
    if (
      versionsData.versions.rest.postStatus === HttpFetchStatus.Success &&
      isCustomVersionFormOpen
    ) {
      const newVersion = versionsData.versions.data?.find(
        (v) => v.package === versionPackage && v.version === version,
      );
      if (newVersion != null) {
        setValue(newVersion.id);
      }
      updateResetToggle();
      closeCustomVersionFormHandler();
    }
  }, [
    closeCustomVersionFormHandler,
    isCustomVersionFormOpen,
    versionsData.versions.rest.postStatus,
    updateResetToggle,
    versionsData.versions.data,
    versionPackage,
    version,
    setValue,
  ]);

  const closeHandler = () => {
    dispatch(
      resetVersionVersionsRestState({
        postStatus: HttpFetchStatus.Idle,
        postError: null,
      }),
    );
    updateResetToggle();
    closeCustomVersionFormHandler();
  };

  const addCustomVersionClickHandler = () => {
    dispatch(
      createUpdateVersion({
        version,
        package: versionPackage,
      }),
    );
  };

  return (
    <React.Fragment>
      <Grid
        container
        sx={{
          backgroundColor:
            value !== initialValueId && initialValueId != null ? 'rgb(237, 247, 237)' : undefined,
          p: 1,
          borderRadius: 1,
        }}
      >
        {initialValueId != null && (
          <React.Fragment>
            <Grid item xs={12}>
              <InputLabel shrink>{label}</InputLabel>
            </Grid>
            <Grid item xs={3} alignContent="center">
              <Typography>{getVersionById(initialValueId)}</Typography>
            </Grid>
            <Grid item xs={1} alignContent="center">
              <MoveRightArrow />
            </Grid>
          </React.Fragment>
        )}
        <Grid item xs={initialValueId != null ? 8 : 12}>
          {isCustomVersionFormOpen ? (
            <Stack spacing={2}>
              {versionsData.versions.rest.postError !== null &&
                isHttpError(versionsData.versions.rest.postError) && (
                  <CustomAlert
                    severity="error"
                    header={t(versionsData.versions.rest.postError.message)}
                  />
                )}
              <Stack direction="row" alignItems="center" spacing={1}>
                <TextInput
                  value={version}
                  onChange={versionChangeHandler}
                  validationResult={versionValidationResult}
                  label={t('input-label-version-version')}
                />
                <TooltipIconButton
                  onClick={addCustomVersionClickHandler}
                  color="primary"
                  tooltip={t('button-save')}
                  data-testid="button-save"
                  disabled={versionsData.versions.rest.postStatus === HttpFetchStatus.Loading}
                  icon={
                    versionsData.versions.rest.postStatus === HttpFetchStatus.Loading ? (
                      <CircularProgress size={24} />
                    ) : (
                      <SaveIcon />
                    )
                  }
                />
                <IconButton onClick={closeCustomVersionFormHandler}>
                  <CrossCloseIcon />
                </IconButton>
              </Stack>
            </Stack>
          ) : (
            <SelectInput
              label={initialValueId == null && label}
              value={value}
              onChange={onChange}
              validationResult={validationResult}
            >
              <Button
                startIcon={<AddIcon />}
                size="small"
                fullWidth
                onClick={openCustomVersionFormClickHandler}
              >
                {t('button-add-version')}
              </Button>
              {versionsData.versions.data
                ?.filter((v) => v.package === versionPackage)
                .sort((a, b) => sortByDate(a.dateCreated, b.dateCreated))
                .map((vp) => (
                  <MenuItem key={vp.id} value={vp.id}>
                    <Stack direction="row" justifyContent="space-between" width="100%">
                      {vp.version}
                    </Stack>
                  </MenuItem>
                ))}
            </SelectInput>
          )}
        </Grid>
      </Grid>
      <CustomSnackbar
        open={versionsData.versions.rest.postStatus === HttpFetchStatus.Success}
        title={t('success-creating-version')}
        onClose={closeHandler}
      />
    </React.Fragment>
  );
};

export default VersionsForm;
